import { useCallback, useEffect, useState } from 'react';
import { registerQuery, useDataFetchingClient, useQuery } from 'data-fetching-client';
import useFetchObjectFromInboundDb from 'marketing-platform-lib/internal/hooks/useFetchObjectFromInboundDb';
import { fetchCrmObject } from 'framework-asset-preview-lib/internal/shared/api/crm';
const FETCH_CRM_OBJECT_FIELD_NAME = 'previewLibFetchCrmObject';
const FETCH_CRM_OBJECT_QUERY = registerQuery({
  fieldName: FETCH_CRM_OBJECT_FIELD_NAME,
  // intentionally excluding retries
  args: ['objectTypeId', 'objectId'],
  fetcher({
    objectTypeId,
    objectId
  }) {
    return fetchCrmObject(objectId, objectTypeId).then(result => {
      // Apollo treats undefined as a missing field and rejects
      return result !== null && result !== void 0 ? result : null;
    });
  }
});
export default function useFetchCrmObject({
  objectId,
  objectType,
  skip
}) {
  const [optimisticRefetchCrmObject, setOptimisticRefretchCrmObject] = useState();
  const {
    refetch,
    data,
    error,
    loading,
    stopPolling
  } = useQuery(FETCH_CRM_OBJECT_QUERY, {
    variables: {
      objectId: String(objectId),
      objectTypeId: objectType
    },
    pollInterval: 500,
    onCompleted() {
      if (data !== null && data !== void 0 && data[FETCH_CRM_OBJECT_FIELD_NAME]) {
        stopPolling();
      }
    },
    onError() {
      stopPolling();
    },
    skip
  });
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      stopPolling();
    }, 5000);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [stopPolling]);
  useEffect(() => {
    if (data !== null && data !== void 0 && data[FETCH_CRM_OBJECT_FIELD_NAME]) {
      stopPolling();
    }
  }, [data, stopPolling]);
  const crmSearchObject = data === null || data === void 0 ? void 0 : data[FETCH_CRM_OBJECT_FIELD_NAME];
  const {
    data: inboundDbObject
  } = useFetchObjectFromInboundDb({
    objectId,
    objectTypeId: objectType
  }, {
    skip
  });

  // inboundDbObject will be missing association previews and will 404 in certain cases
  // where "objectId" isn't a crm-object id, but something else like hs_origin_asset_id
  // However, it will return a CRMObject faster in cases where it has been recently created
  const crmObjectFromFetch = crmSearchObject || inboundDbObject;
  const refetchOptimistic = useCallback(optimisticCrmObject => {
    setOptimisticRefretchCrmObject(optimisticCrmObject);
    refetch().catch(() => {}).finally(() => {
      setOptimisticRefretchCrmObject(undefined);
    });
  }, [refetch]);
  return {
    data: optimisticRefetchCrmObject || crmObjectFromFetch,
    error,
    loading,
    refetch: refetchOptimistic
  };
}
export function useUpdateFetchCrmObjectCache() {
  const client = useDataFetchingClient();
  return useCallback(({
    objectId,
    objectType,
    updateFn
  }) => {
    const existingData = client.readQuery({
      query: FETCH_CRM_OBJECT_QUERY,
      variables: {
        objectId: String(objectId),
        objectTypeId: objectType
      }
    });
    const updatedCrmObject = updateFn(existingData === null || existingData === void 0 ? void 0 : existingData[FETCH_CRM_OBJECT_FIELD_NAME]);
    client.writeQuery({
      query: FETCH_CRM_OBJECT_QUERY,
      variables: {
        objectId: String(objectId),
        objectTypeId: objectType
      },
      data: {
        [FETCH_CRM_OBJECT_FIELD_NAME]: updatedCrmObject
      }
    });
    return updatedCrmObject;
  }, [client]);
}